// src/pages/Terms.jsx
import { Link } from "react-router-dom";
import { FileText, Shield, Mail, MapPin } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900">{title}</h3>
    <div className="text-stone-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

export const Terms = () => {
  const lastUpdated = "February 22, 2026";

  return (
    <div className="bg-white text-stone-900">
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950 via-blue-950 to-black" />
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1800&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm text-white/90">
              <FileText className="w-4 h-4" />
              <span>Website Terms</span>
            </div>

            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mt-6 leading-[1.05]">
              Terms of Service
            </h1>

            <p className="text-white/85 text-base md:text-lg mt-4 max-w-2xl">
              These Terms govern how you access and use GLGIF’s website, content, and online services.
            </p>

            <p className="text-white/70 text-sm mt-4">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Main */}
            <div className="lg:col-span-8 space-y-10">
              <Card className="rounded-2xl border-stone-200/70 shadow-sm">
                <CardContent className="p-7 md:p-8 space-y-10">
                  <Section title="1. Acceptance of Terms">
                    <p>
                      By accessing or using this website, you agree to these Terms and our{" "}
                      <Link to="/privacy" className="text-amber-700 font-medium hover:text-amber-800">
                        Privacy Policy
                      </Link>
                      . If you do not agree, please do not use the site.
                    </p>
                  </Section>

                  <Section title="2. About GLGIF">
                    <p>
                      GoldenLife Global Impact Foundation (GLGIF) is a mission-driven organization committed to dignity,
                      protection, humanitarian support, and long-term empowerment across communities.
                    </p>
                  </Section>

                  <Section title="3. Website Use & Conduct">
                    <p>You agree not to:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Use the website for unlawful, harmful, or misleading activities.</li>
                      <li>Attempt to gain unauthorized access to the website, servers, or data.</li>
                      <li>Submit false information (including fake donations, volunteer applications, or contact forms).</li>
                      <li>Upload malicious code or abuse features that disrupt normal use.</li>
                    </ul>
                  </Section>

                  <Section title="4. Content & Intellectual Property">
                    <p>
                      GLGIF’s website content (text, branding, graphics, media, and materials) is owned by GLGIF or used
                      with permission. You may share public content for awareness with proper credit, but you may not
                      copy, sell, republish, or commercially exploit our content without written permission.
                    </p>
                  </Section>

                  <Section title="5. Donations, Volunteers & Partnerships">
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <b>Donations:</b> Donations help us deliver humanitarian relief and empowerment projects. Where
                        payments are processed by third-party providers, your payment details may be handled by them
                        under their own terms.
                      </li>
                      <li>
                        <b>Volunteer & Partner forms:</b> Submitting a form does not automatically create an agreement.
                        We will review and contact you.
                      </li>
                      <li>
                        <b>Impact reporting:</b> We share impact updates and stories to promote accountability. We aim to
                        do this respectfully and with dignity.
                      </li>
                    </ul>
                  </Section>

                  <Section title="6. Newsletter & Communications">
                    <p>
                      If you subscribe to our newsletter, you agree to receive campaign updates and impact stories. You
                      may unsubscribe at any time.
                    </p>
                  </Section>

                  <Section title="7. External Links">
                    <p>
                      Our website may contain links to third-party sites (e.g., social media, payment providers). GLGIF is
                      not responsible for third-party content, policies, or practices.
                    </p>
                  </Section>

                  <Section title="8. Disclaimers">
                    <div className="rounded-xl border border-stone-200 bg-stone-50 p-5">
                      <p className="flex items-start gap-2">
                        <Shield className="w-5 h-5 text-amber-700 mt-0.5" />
                        <span>
                          We make reasonable efforts to keep website information accurate and available, but the site is
                          provided “as is.” We do not guarantee uninterrupted access or that every page will be error-free
                          at all times.
                        </span>
                      </p>
                    </div>
                  </Section>

                  <Section title="9. Limitation of Liability">
                    <p>
                      To the extent permitted by law, GLGIF will not be liable for indirect, incidental, or consequential
                      damages arising from the use (or inability to use) this website.
                    </p>
                  </Section>

                  <Section title="10. Changes to These Terms">
                    <p>
                      We may update these Terms occasionally to reflect improvements, legal needs, or operational changes.
                      Updates will be posted here with a revised “Last updated” date.
                    </p>
                  </Section>

                  <Section title="11. Contact Us">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-700" />
                      <span>Uyo, Akwa Ibom State, Nigeria</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-amber-700" />
                      <span>info@glgif.org</span>
                    </p>
                  </Section>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-6">
                  <Link to="/contact">Contact GLGIF</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-2 border-stone-200 text-stone-800 px-6"
                >
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            </div>

            {/* Side */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">
                <Card className="rounded-2xl border-stone-200/70 shadow-sm">
                  <CardContent className="p-6">
                    <p className="font-heading text-lg font-bold text-stone-900 mb-2">Quick Summary</p>
                    <ul className="text-stone-700 text-sm space-y-2 list-disc pl-5">
                      <li>Use the website responsibly and lawfully.</li>
                      <li>Donations and forms are handled with care and accountability.</li>
                      <li>We may update these terms from time to time.</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-stone-200/70 shadow-sm">
                  <CardContent className="p-6">
                    <p className="font-heading text-lg font-bold text-stone-900 mb-2">Related</p>
                    <div className="space-y-2">
                      <Link to="/privacy" className="text-amber-700 hover:text-amber-800 font-medium">
                        View Privacy Policy →
                      </Link>
                      <div className="text-stone-600 text-sm">
                        Learn how we collect, use, and protect your information.
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;